/**
 * One homeowner limit for the plan card and for enforcement.
 * Solo is 200 per agent. A stored client_limit of 100 is the old default
 * and is not used. A stored solo limit above the add-on floor is a custom
 * allowance and is kept. A team pool is 200 homeowners per purchased seat
 * (teams.max_seats) when the add-on is None, shared by the owner and every
 * member. Pass the team row when you have it. Without that row, a team plan
 * still uses a stored client_limit of at least the per-agent floor (checkout
 * writes 200 per seat onto the owner).
 * Plus raises that floor to 500 per agent. Max raises it to 1,000 per agent.
 * On a team the add-on is the team's, billed per seat, so 5 seats on Max is
 * a shared pool of 5,000. A member's own add-on does not raise the pool.
 * Enterprise keeps its custom client_limit. On a team, that custom number
 * is the shared pool instead of the per-seat floor.
 *
 * An active homeowner counts toward the limit: status invited (invited, not
 * signed up yet) or active (signed up). inactive and archived do not count.
 * The same statuses are used in supabase/migrations/20261015120000_homeowner_addon.sql.
 *
 * Warnings start at 80% and again at 100% of the plan limit. New adds pause
 * after a 10% grace (limit + floor(limit / 10)). Existing homeowners stay.
 *
 * Switching from team to solo removes the team add-on. For
 * HOMEOWNER_TEAM_SOLO_GRACE_DAYS (default 30, env override of the same name)
 * the limit stays at that agent's active homeowner count, and at least 200.
 * After homeowner_grace_ends_at the normal solo or add-on limit applies.
 * Rows already stored stay.
 */

const SOLO_HOMEOWNER_LIMIT = 200;
const HOMEOWNERS_PER_SEAT = 200;
const HOMEOWNER_ADDON_PLUS = 500;
const HOMEOWNER_ADDON_MAX = 1000;
const HOMEOWNER_ADDON_CONTACT = 'support@pacarion.com';
const ACTIVE_HOMEOWNER_STATUSES = ['invited', 'active'];
const HOMEOWNER_TEAM_SOLO_GRACE_DAYS = 30;

function teamSoloGraceDays(env = process.env) {
  const raw = env && env.HOMEOWNER_TEAM_SOLO_GRACE_DAYS;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return HOMEOWNER_TEAM_SOLO_GRACE_DAYS;
  }
  const days = parseInt(raw, 10);
  if (!Number.isFinite(days) || days < 0) return HOMEOWNER_TEAM_SOLO_GRACE_DAYS;
  return days;
}

function teamSoloLandingLimit(_teamEraLimit, activeCount) {
  // The first argument is the pool at the switch. Grace is not capped by it.
  const count = Math.floor(Number(activeCount));
  const countOk = Number.isFinite(count) && count > 0 ? count : 0;
  return Math.max(countOk, 200);
}

function graceIsActive(endsAt, now = new Date()) {
  if (!endsAt) return false;
  const end = new Date(endsAt).getTime();
  const at = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(end) || !Number.isFinite(at)) return false;
  return at < end;
}

function graceDaysLeft(endsAt, now = new Date()) {
  if (!graceIsActive(endsAt, now)) return 0;
  const end = new Date(endsAt).getTime();
  const at = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return Math.max(1, Math.ceil((end - at) / 86400000));
}

function effectiveHomeownerLimit(normalLimit, graceLimit, endsAt, now) {
  const normal = Number(normalLimit);
  const base = Number.isFinite(normal) && normal > 0 ? normal : 0;
  if (!graceIsActive(endsAt, now)) return base;
  const landing = Math.floor(Number(graceLimit));
  const extra = Number.isFinite(landing) && landing > 0 ? landing : 0;
  return Math.max(base, extra);
}

const HOMEOWNER_ADDONS = {
  none: { id: 'none', label: 'None', monthlyCents: 0, perAgent: HOMEOWNERS_PER_SEAT },
  plus: { id: 'plus', label: 'Plus', monthlyCents: 1000, perAgent: HOMEOWNER_ADDON_PLUS },
  max: { id: 'max', label: 'Max', monthlyCents: 2500, perAgent: HOMEOWNER_ADDON_MAX },
};

function storedLimit(profile) {
  const value = Number(profile && profile.client_limit);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function onTeam(profile, team) {
  return Boolean((profile && profile.team_id) || (team && team.id));
}

function purchasedSeats(team) {
  const seats = Number(team && team.max_seats);
  if (!Number.isFinite(seats) || seats < 1) return 0;
  return Math.floor(seats);
}

function normalizeAddon(value) {
  const id = String(value || 'none').trim().toLowerCase();
  return HOMEOWNER_ADDONS[id] ? id : 'none';
}

function addonSpec(value) {
  return HOMEOWNER_ADDONS[normalizeAddon(value)];
}

function seatCount(seats, shared) {
  if (!shared) return 1;
  const count = Math.floor(Number(seats) || 0);
  return count > 0 ? count : 1;
}

/**
 * Active homeowners are invited or signed up.
 * A missing status counts as invited, matching the clients.status default.
 * Archived and inactive rows do not count.
 */
function isActiveHomeowner(client) {
  const status = String((client && client.status) || 'invited').trim().toLowerCase();
  return ACTIVE_HOMEOWNER_STATUSES.indexOf(status) !== -1;
}

function resolvedAddon(profile, team) {
  if (onTeam(profile, team) && team) return normalizeAddon(team.homeowner_addon);
  return normalizeAddon(profile && profile.homeowner_addon);
}

function planHomeownerLimit(profile, team, now) {
  const stored = storedLimit(profile);
  const plan = profile && profile.subscription_plan;
  let limit;
  if (plan === 'enterprise') {
    limit = stored > 0 ? stored : SOLO_HOMEOWNER_LIMIT;
  } else {
    const perAgent = addonSpec(resolvedAddon(profile, team)).perAgent;
    if (onTeam(profile, team) || plan === 'team') {
      const seats = purchasedSeats(team);
      limit = seats > 0 ? seats * perAgent : (stored >= perAgent ? stored : perAgent);
    } else {
      limit = stored > perAgent ? stored : perAgent;
    }
  }
  if (onTeam(profile, team)) return limit;
  return effectiveHomeownerLimit(
    limit,
    profile && profile.homeowner_grace_limit,
    profile && profile.homeowner_grace_ends_at,
    now
  );
}

/** Archived homeowners drop out of the count and keep the row. */
function restoredHomeownerStatus(client) {
  if (client && (client.user_id || client.account_created)) return 'active';
  return 'invited';
}

/**
 * Banner while a team-to-solo landing is still open.
 * used null means the count is not loaded yet.
 */
function homeownerGraceView(profile, used, now = new Date()) {
  if (!profile || profile.team_id) return null;
  if (!graceIsActive(profile.homeowner_grace_ends_at, now)) return null;
  const days = graceDaysLeft(profile.homeowner_grace_ends_at, now);
  const hasCount = used !== null && used !== undefined && used !== '';
  const shown = hasCount ? (Number(used) || 0) : null;
  const dayLabel = days === 1 ? '1 day' : `${days} days`;
  const homeownerLabel = shown === 1 ? '1 homeowner' : `${shown} homeowners`;
  const countLabel = shown === null
    ? `Solo includes ${SOLO_HOMEOWNER_LIMIT} homeowners.`
    : `You have ${homeownerLabel}, and Solo includes ${SOLO_HOMEOWNER_LIMIT}.`;
  const landing = Math.floor(Number(profile.homeowner_grace_limit));
  const landingLabel = Number.isFinite(landing) && landing > SOLO_HOMEOWNER_LIMIT
    ? ` Your limit stays at ${landing} until then, so nothing is blocked.`
    : '';
  const showActions = shown === null || shown > SOLO_HOMEOWNER_LIMIT;
  const advice = showActions ? ' Buy Plus or Max, or archive homeowners.' : '';
  return {
    days,
    used: shown,
    soloLimit: SOLO_HOMEOWNER_LIMIT,
    showActions,
    message: `${dayLabel} left before Solo limits apply.${landingLabel} ${countLabel}${advice} After this, new adds and invites pause if you are over your Solo or add-on limit. Existing homeowners keep access.`,
  };
}

/** New adds pause at the plan limit plus 10%, counted in whole homeowners. */
function homeownerPauseAt(limit) {
  const cap = Number(limit);
  if (!Number.isFinite(cap) || cap <= 0) return 0;
  return cap + Math.floor(cap / 10);
}

function homeownerUsageLevel(used, limit) {
  const count = Number(used) || 0;
  const cap = Number(limit);
  if (!Number.isFinite(cap) || cap <= 0) return 'ok';
  if (count >= homeownerPauseAt(cap)) return 'paused';
  if (count >= cap) return 'full';
  if (count * 10 >= cap * 8) return 'warn';
  return 'ok';
}

function homeownerUsageWarning(used, limit, shared) {
  const level = homeownerUsageLevel(used, limit);
  if (level === 'ok') return '';
  const count = formatHomeownerCount(used);
  const cap = formatHomeownerCount(limit);
  if (level === 'warn') {
    return shared
      ? `Your team is at 80% of its homeowner limit (${count} of ${cap}). New homeowners can still be added.`
      : `You're at 80% of your homeowner limit (${count} of ${cap}). New homeowners can still be added.`;
  }
  if (level === 'full') {
    return shared
      ? `Your team is at its homeowner limit (${count} of ${cap}). A few more can still be added, then new homeowners pause. Existing homeowners keep access.`
      : `You're at your homeowner limit (${count} of ${cap}). A few more can still be added, then new homeowners pause. Existing homeowners keep access.`;
  }
  return shared
    ? 'New homeowners are paused for this team. Existing homeowners keep access.'
    : 'New homeowners are paused. Existing homeowners keep access.';
}

function limitReachedMessage(limit, shared, addon) {
  const pause = homeownerPauseAt(limit);
  const who = shared ? 'Your team has paused new homeowners' : "You've paused new homeowners";
  let text = `${who} at ${pause}. Existing homeowners keep access.`;
  if (normalizeAddon(addon) === 'max') {
    text += ` Need more than ${HOMEOWNER_ADDON_MAX.toLocaleString('en-US')} per agent? Contact us at ${HOMEOWNER_ADDON_CONTACT}.`;
  }
  return text;
}

function seatMonthlyTotal(basePrice, seats, addon) {
  const base = Number(basePrice);
  if (!Number.isFinite(base)) return null;
  return base + addonQuote({ addon, seats, shared: true }).monthly;
}

function addonQuote({ addon, seats, shared }) {
  const spec = addonSpec(addon);
  const count = seatCount(seats, shared);
  const monthly = spec.id === 'none' ? 0 : (spec.monthlyCents / 100) * count;
  return {
    addon: spec.id,
    label: spec.label,
    perAgent: spec.perAgent,
    monthlyPerAgent: spec.monthlyCents / 100,
    seats: count,
    pool: spec.perAgent * count,
    monthly,
    shared: Boolean(shared),
  };
}

function addonConfirmMessage({ addon, seats, shared }) {
  const quote = addonQuote({ addon, seats, shared });
  const limitText = quote.shared
    ? `${quote.pool.toLocaleString('en-US')} homeowners, shared by the team`
    : `${quote.perAgent.toLocaleString('en-US')} homeowners`;
  if (quote.addon === 'none') {
    return `Remove the homeowner add-on?\n\nYour limit goes back to ${limitText}.\n\nYour billing will be prorated automatically.`;
  }
  const price = quote.shared && quote.seats > 1
    ? `+$${quote.monthly}/mo (+$${quote.monthlyPerAgent}/mo per agent × ${quote.seats} seats)`
    : `+$${quote.monthly}/mo`;
  return `Change to ${quote.label}?\n\n${quote.label} is ${price}.\nYou'll be able to manage up to ${limitText}.\n\nYour billing will be prorated automatically.`;
}

function addonChoiceLabel(quote) {
  if (!quote || quote.addon === 'none') {
    return quote && quote.shared
      ? `None, included, ${quote.pool.toLocaleString('en-US')} homeowners shared`
      : `None, included, ${(quote ? quote.perAgent : HOMEOWNERS_PER_SEAT).toLocaleString('en-US')} homeowners`;
  }
  const limit = quote.shared
    ? `${quote.pool.toLocaleString('en-US')} homeowners shared`
    : `${quote.perAgent.toLocaleString('en-US')} homeowners`;
  return `${quote.label}, +$${quote.monthlyPerAgent}/mo per agent, ${limit}`;
}

/**
 * Change Plan add-on copy. Stripe TEST price ids are not hardcoded here.
 * Functions read STRIPE_HOMEOWNER_PLUS_PRICE and STRIPE_HOMEOWNER_MAX_PRICE,
 * then REACT_APP_STRIPE_HOMEOWNER_PLUS_PRICE and REACT_APP_STRIPE_HOMEOWNER_MAX_PRICE.
 * Those test prices are created by scripts/create-stripe-homeowner-addon-prices.js
 * (PR #133). This copy does not create prices and does not read a live key.
 */
function changePlanAddonLines() {
  const plus = addonSpec('plus');
  const max = addonSpec('max');
  const price = (spec) => `+$${spec.monthlyCents / 100}/mo`;
  const homes = (spec) => spec.perAgent.toLocaleString('en-US');
  return [
    `Plus: ${price(plus)} per agent, up to ${homes(plus)} homeowners`,
    `Max: ${price(max)} per agent, up to ${homes(max)} homeowners (hard cap)`,
  ];
}

function changePlanAddonNote() {
  return `For Team, add-ons are per seat and raise the shared pool (base ${HOMEOWNERS_PER_SEAT} per seat). Only active homeowners count.`;
}

function formatHomeownerCount(value) {
  const count = Number(value);
  const shown = Number.isFinite(count) ? count : 0;
  return shown.toLocaleString('en-US');
}

function homeownerUsageLabel(used, limit, shared) {
  const base = `${formatHomeownerCount(used)} of ${formatHomeownerCount(limit)} homeowners`;
  return shared ? `${base}, shared by the team` : base;
}

function homeownerPoolRow(data) {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || row.pool_limit == null || row.pool_limit === '') return null;
  const limit = Number(row.pool_limit);
  if (!Number.isFinite(limit)) return null;
  return {
    used: Number(row.used) || 0,
    limit,
    shared: row.shared === true,
    addon: normalizeAddon(row.addon),
  };
}

export {
  SOLO_HOMEOWNER_LIMIT,
  HOMEOWNERS_PER_SEAT,
  HOMEOWNER_ADDON_PLUS,
  HOMEOWNER_ADDON_MAX,
  HOMEOWNER_ADDON_CONTACT,
  ACTIVE_HOMEOWNER_STATUSES,
  HOMEOWNER_TEAM_SOLO_GRACE_DAYS,
  teamSoloGraceDays,
  teamSoloLandingLimit,
  graceIsActive,
  graceDaysLeft,
  effectiveHomeownerLimit,
  restoredHomeownerStatus,
  homeownerGraceView,
  HOMEOWNER_ADDONS,
  normalizeAddon,
  addonSpec,
  isActiveHomeowner,
  purchasedSeats,
  planHomeownerLimit,
  homeownerPauseAt,
  homeownerUsageLevel,
  homeownerUsageWarning,
  limitReachedMessage,
  addonQuote,
  seatMonthlyTotal,
  addonConfirmMessage,
  addonChoiceLabel,
  changePlanAddonLines,
  changePlanAddonNote,
  formatHomeownerCount,
  homeownerUsageLabel,
  homeownerPoolRow,
};
